import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGetContacts, apiCreateContact, apiUpdateContact, apiGetVenues, apiGetBands, apiAddContactBand, apiRemoveContactBand } from '../api';
import { nameToSlug } from '../utils/slug';
import type { ContactDTO, VenueDTO } from '../api';
import AutoTextarea from '../components/AutoTextarea';
import PageHeader from '../components/PageHeader';
import { useConfig } from '../hooks/useConfig';
import { reportSaveError, reportSaveOk } from '../lib/saveStatus';

// Fallback role suggestions if the band has no editable list yet.
const FALLBACK_ROLES = ['Stage manager', 'Backstage / hospitality', 'Runner', 'Artist liaison', 'Promoter', 'Driver', 'Security', 'Other'];

export default function ContactEditPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new' || !id;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { config } = useConfig();

  const [contact, setContact] = useState<ContactDTO | null>(null);
  const [venues, setVenues] = useState<VenueDTO[]>([]);
  const [bands, setBands] = useState<{ id: string; name: string }[]>([]);
  const [bandIds, setBandIds] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // Snapshot of the last persisted contact (server round-trip), used to skip
  // the debounced autosave when nothing changed since load/save — prevents a
  // redundant PUT right after load and any wipe-on-load-error (2026-08-26).
  const lastSavedRef = useRef<string>('');

  useEffect(() => {
    let cancelled = false;
    setLoadError(false);
    Promise.all([apiGetContacts(), apiGetVenues(), apiGetBands()])
      .then(([contacts, vs, bs]) => {
        if (cancelled) return;
        setVenues(vs);
        setBands(bs.map((b: any) => ({ id: b.id, name: b.name })));
        const distinctRoles = Array.from(new Set(contacts.map(c => c.role).filter((r): r is string => !!r)));
        setRoles(distinctRoles);
        if (isNew) {
        setContact({ id: '', name: '', lastName: '', role: '', email: '', phone: '', venue_id: '', notes: '' });
        } else {
        const existing = contacts.find((c) => c.id === id) || contacts.find((c) => nameToSlug(c.name) === id);
        setContact(existing || { id: '', name: '', lastName: '', role: '', email: '', phone: '', venue_id: '', notes: '' });
        setBandIds(existing?.band_ids || []);
        // Snapshot what we actually loaded so the autosave below skips the
        // redundant PUT and never wipes real data on a transient load error.
        if (existing) lastSavedRef.current = JSON.stringify(existing);
        }
        setLoading(false);
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) { setLoading(false); setLoadError(true); }
      });
    return () => { cancelled = true; };
  }, [id, isNew]);

  // ---- Autosave (debounced) ----
  // Text fields (name, lastName, role, email, phone, venue_id, notes) persist
  // automatically via apiUpdateContact after a 500ms debounce, like the team
  // pages. The band_ids toggle saves immediately via its own API calls and is
  // deliberately left untouched here. The "Save Changes" button remains as an
  // immediate save + navigate.
  useEffect(() => {
    if (isNew || !contact?.id) return;
    const snap = JSON.stringify(contact);
    if (snap === lastSavedRef.current) return;
    const timer = setTimeout(async () => {
      try {
        const saved = await apiUpdateContact(contact.id, contact);
        lastSavedRef.current = JSON.stringify(saved);
        reportSaveOk('contact');
      } catch (err) {
        reportSaveError('contact', err);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [contact, isNew]);

  const handleChange = useCallback(<K extends keyof ContactDTO>(field: K, value: ContactDTO[K]) => {
    setContact((prev) => (prev ? { ...prev, [field]: value } : prev));
  }, []);

  // Toggle this contact's visibility in a band. Removing the last link makes
  // the contact shared/global again (visible to every band).
  const toggleBand = useCallback(async (bandId: string) => {
    if (!contact) return;
    const has = bandIds.includes(bandId);
    setBandIds((prev) => (has ? prev.filter((b) => b !== bandId) : [...prev, bandId]));
    try {
      if (has) {
        await apiRemoveContactBand(contact.id, bandId);
      } else {
        await apiAddContactBand(contact.id, bandId);
      }
    } catch (e: any) {
      alert(t('contactEdit.bandUpdateFailed', { msg: e.message }));
      // Revert on error.
      setBandIds((prev) => (has ? [...prev, bandId] : prev.filter((b) => b !== bandId)));
    }
  }, [contact, bandIds]);

  const handleSave = async () => {
    if (!contact) return;
    if (!contact.name.trim()) {
      alert(t('contactEdit.enterName'));
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await apiCreateContact(contact);
      } else {
        await apiUpdateContact(contact.id, contact);
      }
      navigate('/tour/contacts');
    } catch (err) {
      reportSaveError('contact', err);
      alert(t('contactEdit.failedSave'));
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <div className="page" style={{ padding: '48px', textAlign: 'center' }}>
        <p style={{ color: 'var(--danger)', marginBottom: 12 }}>{t('contactEdit.loadFailed')}</p>
        <button className="btn btn-secondary" data-edit-back onClick={() => navigate('/tour/contacts')}>
          {t('contactEdit.cancel')}
        </button>
      </div>
    );
  }

  if (loading || !contact) {
    return (
      <div className="page" style={{ padding: '48px', textAlign: 'center' }}>
        <p style={{ color: 'var(--text-dim)' }}>{t('contactEdit.loading')}</p>
      </div>
    );
  }

  return (
    <div className="page musician-edit-page" data-edit-page="contact" data-density="socle">
      <PageHeader
        title={isNew ? t('contactEdit.newContact') : t('contactEdit.editTitle', { name: contact.name || t('contactEdit.unnamed') })}
      />

      <div className="edit-form">
        {/* Contact Info */}
        <div className="form-section">
          <h2>{t('contactEdit.contactInfo')}</h2>
          <div className="form-row rc-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('contactEdit.name')}</label>
              <input
                type="text"
                data-edit-field="name"
                value={contact.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder={t('contactEdit.namePh')}
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('contactEdit.lastName')}</label>
              <input
                type="text"
                value={contact.lastName || ''}
                onChange={(e) => handleChange('lastName', e.target.value)}
                placeholder={t('contactEdit.lastNamePh')}
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('contactEdit.role')}</label>
              <input
                type="text"
                list="contact-roles"
                value={contact.role || ''}
                onChange={(e) => handleChange('role', e.target.value)}
                placeholder={t('contactEdit.rolePh')}
              />
              <datalist id="contact-roles">
                {Array.from(new Set([...roles, ...(config.rider?.contactRoles?.length ? config.rider.contactRoles : FALLBACK_ROLES)])).map((r) => <option key={r} value={r} />)}
              </datalist>
            </div>
          </div>

          <div className="form-row rc-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('contactEdit.email')}</label>
              <input
                type="text"
                value={contact.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder={t('contactEdit.emailPh')}
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('contactEdit.phone')}</label>
              <input
                type="text"
                value={contact.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder={t('contactEdit.phonePh')}
              />
            </div>
          </div>

          <div className="form-row rc-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('contactEdit.venue')}</label>
              <select
                value={contact.venue_id || ''}
                onChange={(e) => handleChange('venue_id', e.target.value)}
              >
                <option value="">{t('contactEdit.noVenue')}</option>
                {venues.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </div>
          </div>

          {!isNew && (
            <div className="form-group">
              <label>{t('contactEdit.visibleInBands')}</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
                {bands.map((b) => (
                  <label key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={bandIds.includes(b.id)}
                      onChange={() => toggleBand(b.id)}
                    />
                    {b.name}
                  </label>
                ))}
              </div>
              <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '6px 0 0' }}>
                {bandIds.length === 0
                  ? t('contactEdit.sharedGlobal')
                  : t('contactEdit.selectedBands')}
              </p>
            </div>
          )}

          <div className="form-group">
            <label>{t('contactEdit.notes')}</label>
            <AutoTextarea
              rows={3}
              value={contact.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder={t('contactEdit.notesPh')}
            />
          </div>
        </div>

        <div className="form-actions">
          <button className="btn btn-secondary" data-edit-back onClick={() => navigate('/tour/contacts')}>
            {t('contactEdit.cancel')}
          </button>
          <button
            className="btn btn-primary"
            data-edit-save
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? t('contactEdit.saving') : isNew ? t('contactEdit.create') : t('contactEdit.saveChanges')}
          </button>
        </div>
      </div>
    </div>
  );
}